/**
 * /about route - GNPC critical "publication identity" page.
 *
 * Phase 2 Track A revamp (2026-05-22):
 *   Direction A "Tu phap restraint" editorial revamp.
 *   - Hero: serif h1 + subhead 1 sentence + 2px navy left rule.
 *   - Section 1 Mission: editorial paragraph + footnote markers [1][2].
 *   - Section 2 How it works: 3-step numbered list (Question -> VBPL search
 *     + Article/Clause -> Cited answer).
 *   - Section 3 Compliance: render as compliance-card with navy left rule.
 *   - Section 4 Team: placeholder "Dang xay dung" (POC phase).
 *   - Section 5 Contact: editorial list with navy link color.
 *   - All section divided by .section-divider-editorial thin 1px rule.
 *
 * Spec: GTM/GNPC-APPLICATION-KIT.md s3.1 + s7. Single-locale render via
 * useLang() per legal-upl rule (no mixed VN+EN disclaimers).
 *
 * Locked per DECISIONS s2026-05-22-UI-REDESIGN-V4-DIRECTION-A.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../use-lang';
import { resolveAbout } from '../i18n/about';

export function AboutRoute() {
  const { lang } = useLang();
  const copy = useMemo(() => resolveAbout(lang), [lang]);

  useEffect(() => {
    document.title = copy.meta_title;
    setMeta('description', copy.meta_description);
    setMeta('og:title', copy.meta_title, true);
    setMeta('og:description', copy.meta_description, true);
  }, [copy]);

  const heroSub =
    lang === 'en'
      ? 'AI legal copilot for Vietnam - reference tool grounded in the official VBPL corpus, NOT a substitute for a licensed lawyer.'
      : 'Trợ lý AI pháp luật Việt Nam - tham khảo từ kho VBPL chính thức, KHÔNG thay thế luật sư.';

  return (
    <main id="main" style={{ maxWidth: 760, margin: '32px auto 96px', padding: '0 16px' }}>
      <a className="skip-link" href="#about-mission">
        {lang === 'en' ? 'Skip to content' : 'Bỏ qua đến nội dung'}
      </a>

      {/* ---------- Hero ---------- */}
      <section
        className="hero-editorial animate-rise"
        aria-labelledby="about-hero-heading"
        style={{ marginBottom: 'var(--space-6)' }}
      >
        <div className="editorial-column">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'ABOUT ASKLAW' : 'VỀ ASKLAW'}
          </p>
          <h1 id="about-hero-heading" className="h-display-serif">
            {copy.page_title}
          </h1>
          <p className="body-lead-editorial" style={{ marginTop: 'var(--space-5)' }}>
            {heroSub}
          </p>
        </div>
      </section>

      <div className="editorial-column">
        {/* ---------- Section 1: Mission ---------- */}
        <section id="about-mission" aria-labelledby="about-mission-heading">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'SECTION 01' : 'PHẦN 01'}
          </p>
          <h2 id="about-mission-heading" className="h-section-serif" style={{ marginTop: 'var(--space-3)' }}>
            {copy.mission_heading}
          </h2>
          <p style={{ marginTop: 'var(--space-5)', fontSize: 16, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)' }}>
            {copy.intro}
          </p>
          <p style={{ marginTop: 'var(--space-4)', fontSize: 16, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)' }}>
            {copy.mission_body}
          </p>
          <p style={{ marginTop: 'var(--space-4)', fontSize: 16, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)' }}>
            {lang === 'en' ? (
              <>
                The current POC indexes 54,323 central VBPL documents
                <a href="#about-cite-1" className="cite-marker">[1]</a>
                {' '}with 198,349 citation edges
                <a href="#about-cite-2" className="cite-marker">[2]</a>
                {' '}sourced from the Ministry of Justice corpus.
              </>
            ) : (
              <>
                Mô hình truy vấn quy mô 54.323 văn bản Trung ương
                <a href="#about-cite-1" className="cite-marker">[1]</a>
                {' '}cùng 198.349 liên kết trích dẫn
                <a href="#about-cite-2" className="cite-marker">[2]</a>
                {' '}từ kho VBPL của Bộ Tư pháp.
              </>
            )}
          </p>

          <div className="cite-footnote">
            <span className="cite-footnote-item" id="about-cite-1">
              <span className="cite-footnote-num">[1]</span>
              {lang === 'en'
                ? 'Source: vbpl.moj.gov.vn (Ministry of Justice). Snapshot 2026-05-21.'
                : 'Nguồn: vbpl.moj.gov.vn (Bộ Tư pháp). Bản tổng hợp 2026-05-21.'}
            </span>
            <span className="cite-footnote-item" id="about-cite-2">
              <span className="cite-footnote-num">[2]</span>
              {lang === 'en'
                ? 'Citation graph: 198,349 typed reference edges across 11 verified relation labels.'
                : 'Đồ thị trích dẫn: 198.349 liên kết theo 11 loại quan hệ đã xác thực.'}
            </span>
          </div>
        </section>

        <hr className="section-divider-editorial" />

        {/* ---------- Section 2: How it works ---------- */}
        <section aria-labelledby="about-howit-heading">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'SECTION 02' : 'PHẦN 02'}
          </p>
          <h2 id="about-howit-heading" className="h-section-serif" style={{ marginTop: 'var(--space-3)' }}>
            {lang === 'en' ? 'How Asklaw works' : 'Cách Asklaw hoạt động'}
          </h2>

          <ol className="howit-list">
            <li className="howit-item">
              <span className="howit-num" aria-hidden>1</span>
              <div className="howit-body">
                <h3 className="howit-title">
                  {lang === 'en' ? 'You ask a question' : 'Bạn đặt câu hỏi'}
                </h3>
                <p className="howit-desc">
                  {lang === 'en'
                    ? 'Type a question about Vietnamese law in plain language. No legal jargon required.'
                    : 'Đặt câu hỏi về pháp luật Việt Nam bằng ngôn ngữ thường ngày. Không cần thuật ngữ pháp lý.'}
                </p>
              </div>
            </li>
            <li className="howit-item">
              <span className="howit-num" aria-hidden>2</span>
              <div className="howit-body">
                <h3 className="howit-title">
                  {lang === 'en'
                    ? 'Asklaw searches the VBPL corpus + pins Article/Clause'
                    : 'Asklaw tìm trong kho VBPL + ghim Điều/Khoản'}
                </h3>
                <p className="howit-desc">
                  {lang === 'en'
                    ? 'The AI retrieves relevant central documents from vbpl.moj.gov.vn and identifies the exact Article + Clause that applies.'
                    : 'AI truy xuất các văn bản Trung ương liên quan từ vbpl.moj.gov.vn và xác định chính xác Điều + Khoản áp dụng.'}
                </p>
              </div>
            </li>
            <li className="howit-item">
              <span className="howit-num" aria-hidden>3</span>
              <div className="howit-body">
                <h3 className="howit-title">
                  {lang === 'en'
                    ? 'You get an answer with citations you can verify'
                    : 'Bạn nhận câu trả lời kèm trích dẫn có thể kiểm tra'}
                </h3>
                <p className="howit-desc">
                  {lang === 'en'
                    ? 'Every answer cites Article + Clause. Click to open the original document on vbpl.moj.gov.vn for full text.'
                    : 'Mỗi câu trả lời đều dẫn Điều + Khoản. Bấm để mở văn bản gốc trên vbpl.moj.gov.vn xem toàn văn.'}
                </p>
              </div>
            </li>
          </ol>
        </section>

        <hr className="section-divider-editorial" />

        {/* ---------- Section 3: What Asklaw is / is NOT ---------- */}
        <section aria-labelledby="about-scope-heading">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'SECTION 03' : 'PHẦN 03'}
          </p>
          <h2 id="about-scope-heading" className="h-section-serif" style={{ marginTop: 'var(--space-3)' }}>
            {lang === 'en' ? 'Scope - what Asklaw is and is NOT' : 'Phạm vi - Asklaw LÀ và KHÔNG phải gì'}
          </h2>

          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 18,
              color: 'var(--asklaw-ink)',
              marginTop: 'var(--space-5)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {copy.is_heading}
          </h3>
          <ul style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)', paddingLeft: 'var(--space-5)', margin: 0 }}>
            <li>{copy.is_item_1}</li>
            <li>{copy.is_item_2}</li>
            <li>{copy.is_item_3}</li>
          </ul>

          <h3
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 18,
              color: 'var(--asklaw-ink)',
              marginTop: 'var(--space-6)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {copy.not_heading}
          </h3>
          <ul style={{ fontSize: 15, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)', paddingLeft: 'var(--space-5)', margin: 0 }}>
            <li>{copy.not_item_1}</li>
            <li>{copy.not_item_2}</li>
            <li>{copy.not_item_3}</li>
          </ul>
        </section>

        <hr className="section-divider-editorial" />

        {/* ---------- Section 4: Compliance (4-luat disclaimer block) ---------- */}
        <section aria-labelledby="about-compliance-heading">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'SECTION 04' : 'PHẦN 04'}
          </p>
          <h2 id="about-compliance-heading" className="h-section-serif" style={{ marginTop: 'var(--space-3)' }}>
            {copy.compliance_heading}
          </h2>

          <p style={{ marginTop: 'var(--space-4)', fontSize: 16, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)' }}>
            {copy.compliance_intro}
          </p>

          <div className="compliance-card" style={{ marginTop: 'var(--space-5)' }}>
            <h3 className="compliance-card-title">
              {lang === 'en' ? 'Four-law compliance framework' : 'Khung tuân thủ 4 luật'}
            </h3>
            <ul className="compliance-card-list">
              <li>{copy.compliance_ai}</li>
              <li>{copy.compliance_pdpa}</li>
              <li>{copy.compliance_upl}</li>
              <li>{copy.compliance_press}</li>
            </ul>
          </div>

          <p
            style={{
              marginTop: 'var(--space-4)',
              fontSize: 13,
              lineHeight: 1.6,
              color: 'var(--asklaw-muted)',
              fontStyle: 'italic',
            }}
          >
            {lang === 'en'
              ? 'Asklaw answers do NOT create a lawyer-client relationship. For specific case advice, consult a licensed Vietnamese lawyer.'
              : 'Câu trả lời của Asklaw KHÔNG tạo lập quan hệ luật sư - khách hàng. Để được tư vấn cho vụ việc cụ thể, vui lòng liên hệ luật sư có chứng chỉ hành nghề tại Việt Nam.'}
          </p>
        </section>

        <hr className="section-divider-editorial" />

        {/* ---------- Section 5: Team (placeholder POC phase) ---------- */}
        <section aria-labelledby="about-team-heading">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'SECTION 05' : 'PHẦN 05'}
          </p>
          <h2 id="about-team-heading" className="h-section-serif" style={{ marginTop: 'var(--space-3)' }}>
            {lang === 'en' ? 'Team' : 'Đội ngũ'}
          </h2>
          <p style={{ marginTop: 'var(--space-4)', fontSize: 16, lineHeight: 1.7, color: 'var(--asklaw-ink-soft)' }}>
            {lang === 'en'
              ? 'Asklaw is built by a small team in Hanoi. Detailed team page is under construction during the POC phase. For media or partnership inquiries, please contact us via the addresses below.'
              : 'Asklaw được xây dựng bởi đội ngũ nhỏ tại Hà Nội. Trang giới thiệu đội ngũ chi tiết đang xây dựng trong giai đoạn POC. Để liên hệ truyền thông hoặc đối tác, vui lòng dùng các kênh liên hệ phía dưới.'}
          </p>
        </section>

        <hr className="section-divider-editorial" />

        {/* ---------- Section 6: Contact ---------- */}
        <section aria-labelledby="about-contact-heading">
          <p className="eyebrow-editorial">
            {lang === 'en' ? 'SECTION 06' : 'PHẦN 06'}
          </p>
          <h2 id="about-contact-heading" className="h-section-serif" style={{ marginTop: 'var(--space-3)' }}>
            {copy.contact_heading}
          </h2>

          <ul className="contact-list">
            <li>
              <strong>{copy.contact_email.split(': ')[0]}:</strong>{' '}
              <a href="mailto:hello@asklaw.vn">hello@asklaw.vn</a>
            </li>
            <li>
              <strong>{copy.contact_dpo.split(': ')[0]}:</strong>{' '}
              <a href="mailto:dpo@asklaw.vn">dpo@asklaw.vn</a>
            </li>
            <li>
              <strong>{copy.contact_legal.split(': ')[0]}:</strong>{' '}
              <a href="mailto:legal@asklaw.vn">legal@asklaw.vn</a>
            </li>
            <li>
              <Link to="/transparency">{copy.contact_transparency_link}</Link>
              {' - '}
              <Link to="/editorial-standards">{copy.contact_editorial_link}</Link>
            </li>
          </ul>
        </section>
      </div>
    </main>
  );
}

function setMeta(name: string, content: string, isProperty = false): void {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.content = content;
}
